import React, {useEffect, useId} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Button, Field, Input, MessageBar, MessageBarBody} from '@fluentui/react-components';
import {
    useGetSavedSearchProductQuery,
    useGetStorefrontQuery
} from '../Api/Api';
import FullPageSpinner from '../Components/Spinner/FullPageSpinner';
import { ProductImage } from "../Components/Helpers/ProductImage";
import {IProduct, ISearchContext} from "../Api/Models/Products";
import AddedToCartDialog from '../Components/Products/AddedToCartDialog';
import {BoxSearchFilled, ShoppingBagAddFilled} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import CAN, {PRICING, CART_MANAGEMENT} from "../Components/Auth/Permissions";
import CurrencyFormat from "../Components/Helpers/CurrencyFormat";
import {useSelector} from "react-redux";
import {RootState} from "../Store/Store";
import {
    useGetPreparedCartQuery
} from "../Api/PreparedCartEndpoints";
import {AddToCartButton} from "./SearchPage";
import BlockedCartNotice from '../Components/Cart/BlockedCartNotice';
import { parseProductPageConfig } from '../Components/Helpers/productPageConfig';
import { elementStyleFrom } from '../Components/Helpers/searchPageConfig';
import CollapsibleDescription from '../Components/Product/CollapsibleDescription';
import ProductImageModal from '../Components/Product/ProductImageModal';
import FavoriteButton from '../Components/Product/FavoriteButton';
import { ProductAttributeChips, ProductTagChips } from '../Components/Product/ProductChips';
import QuotePrice from '../Components/Product/QuotePrice';
import { useLocalization } from '../Components/Helpers/useLocalization';
import PriceQuantityNotice from '../Components/Product/PriceQuantityNotice';
import StockAvailabilityNotice from '../Components/Product/StockAvailabilityNotice';

const SavedSearchProductPage: React.FC = () => {
    const L = useLocalization();
    const { savedSearchId } = useParams<{ savedSearchId: string }>();
    const canSeePricing = CAN(PRICING);
    const canAddToCart = CAN(CART_MANAGEMENT);
    const settings = useSelector((state: RootState) => state.cart);
    // Interest signal: the surface (+ search criteria) the shopper navigated here from, carried as
    // router state by ProductCard/SearchPage's nav() calls. Undefined on a bare visit.
    const location = useLocation();
    const navState = (location.state ?? {}) as { source?: string; searchContext?: ISearchContext };

    const inputId = useId();
    const [quantity, setQuantity] = React.useState<number>(1);
    const [tmpQuantity, setTmpQuantity] = React.useState<string>('1');
    const [imageModalOpen, setImageModalOpen] = React.useState<boolean>(false);
    const product = useGetSavedSearchProductQuery({ savedSearchId: savedSearchId ?? "", source: navState.source, searchContext: navState.searchContext}, {skip: !savedSearchId});
    const sfSettings = useGetStorefrontQuery();
    // Merchant product-page styling (productPageConfig JSON), shared with the normal product page.
    const productConfig = React.useMemo(() => parseProductPageConfig(sfSettings.data?.productPageConfig), [sfSettings.data?.productPageConfig]);
    const containerStyle = React.useMemo(() => elementStyleFrom(productConfig.container), [productConfig.container]);
    const titleStyle = React.useMemo(() => elementStyleFrom(productConfig.title, 'heading'), [productConfig.title]);
    const imageStyle = React.useMemo(() => elementStyleFrom(productConfig.image, 'image'), [productConfig.image]);
    const priceBlockStyle = React.useMemo(() => elementStyleFrom(productConfig.priceBlock), [productConfig.priceBlock]);
    const msrpStyle = React.useMemo(() => elementStyleFrom(productConfig.msrp), [productConfig.msrp]);
    const quantityStyle = React.useMemo(() => elementStyleFrom(productConfig.quantity, 'input'), [productConfig.quantity]);
    const descriptionStyle = React.useMemo(() => elementStyleFrom(productConfig.description, 'body'), [productConfig.description]);
    const addToCartButtonStyle = React.useMemo(() => elementStyleFrom(productConfig.addToCartButton), [productConfig.addToCartButton]);
    const addToCartLabelStyle = React.useMemo(() => elementStyleFrom(productConfig.addToCartLabel, 'label'), [productConfig.addToCartLabel]);
    const tagsStyle = React.useMemo(() => elementStyleFrom(productConfig.tags), [productConfig.tags]);
    const attributesStyle = React.useMemo(() => elementStyleFrom(productConfig.attributes), [productConfig.attributes]);
    // Tags are decorations the storefront opts into; attributes are always shown when the product has them.
    const tagsEnabled = sfSettings.data?.enableProductTags ?? false;

    const [warningMessage, setWarningMessage]= React.useState<string>('');
    const [searchedProduct, setSearchedProduct] = React.useState<IProduct>()
    const [cartDialog, setCartDialog] = React.useState<boolean>(false);
    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});
    const [canActuallyAddToCart, setCanActuallyAddToCart] = React.useState(false);
    const [cartBlocked, setCartBlocked] = React.useState(false);
    const navigate = useNavigate();
    
    useEffect(() => {
        if(settings.selectedCartId && getPreparedCart.data) {
            const canAddToPreparedCart = getPreparedCart.data.canAddAdditionalItems ?? false;
            setCanActuallyAddToCart(canAddToPreparedCart && canAddToCart);
            setCartBlocked(!canAddToPreparedCart);
        }
        else{
            setCanActuallyAddToCart(canAddToCart);
            setCartBlocked(false);
        }
    }, [canAddToCart, getPreparedCart.data, settings.selectedCartId]);

    useEffect(() => {
        if(product.data){
            setSearchedProduct({...product.data})
        }
    }, [product.data]);
    useEffect(() => {
        setTmpQuantity(quantity.toString());
    }, [quantity]);
    
    const trySetQuantity = (quantity: number) => {
        if(quantity <1) {
            setWarningMessage(`Quantity cannot be less than 1`)
            setQuantity(1);
        }
        else if(searchedProduct?.maxQuantity && quantity > searchedProduct.maxQuantity) {
            setWarningMessage(`Only ${searchedProduct.maxQuantity} available for purchase`)
            setQuantity(searchedProduct.maxQuantity)
        }
        else{
            setWarningMessage('');
            setQuantity(quantity);
        }
    }

    const stripNonNumeric = (value: string): string =>{
        return value.replace(/[^0-9]/g, '');
    }

    const trySetTmpQuantity = (value: string) => {
        const numericValue = stripNonNumeric(value);
        if(numericValue === '') {
            setWarningMessage('Quantity cannot be empty');
            setTmpQuantity('');
            return;
        }
        const parsedQuantity = parseInt(numericValue);
        if(isNaN(parsedQuantity)){
            setWarningMessage(`Quantity must be a number`);
            return;
        }
        else if(parsedQuantity < 1) {
            setWarningMessage(`Quantity must be a number greater than 0`);
            setTmpQuantity('1');
        } else {
            setWarningMessage('');
            setTmpQuantity(numericValue);
        }
    }
    
    if (product.isLoading || product.isFetching) return <FullPageSpinner message={L('localization_common_loading')} />

    const blur = () => {
        let parsedQuantity = parseInt(tmpQuantity);
        if(parsedQuantity < 1) {parsedQuantity = 1}
        trySetQuantity(isNaN(parsedQuantity) ? 1 : parsedQuantity);
    }
    

    return (<div className='sf-detail-page' style={{width: '100%', maxWidth: '800px', margin: '0 auto', ...containerStyle}}>
        {searchedProduct ?
        <div>
            <AddedToCartDialog open={cartDialog} onClose={setCartDialog} label={searchedProduct?.name}/>
            <h2 style={{lineHeight: 1.3, ...titleStyle}}>{searchedProduct?.name}</h2>
            <ProductImage
                src={product.data?.image ?? undefined}
                // No real image means the fallback placeholder is showing — nothing worth enlarging.
                className={`product-image${product.data?.image ? ' product-image-zoomable' : ''}`}
                style={imageStyle}
                onClick={product.data?.image ? () => setImageModalOpen(true) : undefined}
            />
            <ProductImageModal
                open={imageModalOpen}
                onOpenChange={setImageModalOpen}
                images={product.data?.image ? [product.data.image] : []}
                index={0}
                onIndexChange={() => {}}
                title={searchedProduct?.name}
            />
            <div className='product-data'>
                {tagsEnabled && <ProductTagChips tags={searchedProduct?.tags} style={tagsStyle} />}
                <ProductAttributeChips
                    attributes={searchedProduct?.attributes}
                    style={attributesStyle}
                    taxonomy={{
                        segment: searchedProduct?.segment,
                        category: searchedProduct?.category,
                        subCategory: searchedProduct?.subCategory,
                    }}
                />
                {searchedProduct?.hidePrice
                    ? <QuotePrice
                        as='h2'
                        className='product-quote-price'
                        style={priceBlockStyle}
                        itemId={searchedProduct.id}
                        itemName={searchedProduct.name}
                        quantity={quantity}
                    />
                    : canSeePricing && searchedProduct?.price &&
                    <h2 className='product-price' style={priceBlockStyle}><CurrencyFormat value={searchedProduct.price}/></h2>
                }
                {(canSeePricing && searchedProduct?.msrp && searchedProduct?.price && searchedProduct.msrp > searchedProduct.price) && <span style={msrpStyle}>List
                    Price: <s><CurrencyFormat value={searchedProduct.msrp}/></s></span>}
                {/* Both qualify the price, so they sit with it rather than below the button — and the
                    quantity ceiling has to be readable before the quantity field, not after it. */}
                <PriceQuantityNotice product={searchedProduct} canSeePricing={canSeePricing} className='product-price-ceiling' style={{ margin: '0.25em 0 0' }} />
                <StockAvailabilityNotice product={searchedProduct} className='product-limited' style={{ margin: '0.25em 0 0' }} />
                <Field style={{marginBottom: '1em', marginTop: '1em'}}>
                    {canActuallyAddToCart &&
                    <Input
                        contentBefore={<>Quantity: </>}
                        style={{width: '100%', textAlign: 'right', ...quantityStyle}}
                        id={inputId}
                        onBlur={blur}
                        value={tmpQuantity}
                        onChange={(_, d) => trySetTmpQuantity(d.value)}
                    />
                    }
                </Field>
                {warningMessage &&
                    <MessageBar intent={"warning"}>
                        <MessageBarBody>
                            {warningMessage}
                        </MessageBarBody>
                    </MessageBar>
                }
                {/* Heart to the LEFT of Add to Cart, on the same line (mirrors the product page). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em' }}>
                    <FavoriteButton product={searchedProduct} source="savedSearch" />
                    {canActuallyAddToCart &&
                    <div
                        onClick={e => e.stopPropagation()}
                        className="card-add-to-cart"
                        style={{
                            zIndex: 3,
                            background: 'white',
                            borderRadius: 13,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                            padding: 0,
                            cursor: 'pointer',
                            maxWidth: '200px',
                        }}
                    >
                        <AddToCartButton product={searchedProduct} onAdd={() => setCartDialog(true)} quantity={quantity} buttonStyle={addToCartButtonStyle} labelStyle={addToCartLabelStyle} source="savedSearch"/>
                    </div>
                    }
                </div>
                {cartBlocked && <BlockedCartNotice />}
            </div>
            <div style={{clear: 'both'}}/>
            <CollapsibleDescription
                markdown={searchedProduct?.description ?? ''}
                className='product-description'
                style={{ margin: '1em 0', ...descriptionStyle }}
                collapsible={!!productConfig.descriptionCollapsible}
                collapsedHeight={productConfig.descriptionCollapsedHeight}
            />
        </div> :
        <div className={'centered-content'}>
            <BoxSearchFilled style={{fontSize: '24em', color: '#ddd', paddingBottom: '20px'}} />
            <p><h1>Product Not Found</h1></p>
            <Button
                appearance='primary'
                onClick={() => navigate('/')}
                icon={<ShoppingBagAddFilled/>} 
                style={{width: '25%', marginTop: '1em', maxWidth: '200px'}}
            >
                {L('localization_common_go_home')}
            </Button>
        </div>}
    </div>);
}

export default SavedSearchProductPage;